import { supabase } from './supabase';
import type { UserRole } from '../types/auth';
import type { SubscriptionTier } from '../types/auth';

export async function checkUserRole(userId: string): Promise<UserRole | null> {
  try {
    console.log('Checking user role for:', userId);
    const { data: userData, error } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .maybeSingle();

    if (error || !userData) {
      console.log('User document not found');
      return null;
    }

    console.log('Found user role:', userData.role);
    return userData.role;
  } catch (error) {
    console.error('Error checking user role:', error);
    return null;
  }
}

export async function isUserCoach(userId: string): Promise<boolean> {
  try {
    const { data: coachData, error: coachError } = await supabase
      .from('coaches')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    return !coachError && !!coachData;
  } catch (error) {
    console.error('Error checking coach status:', error);
    return false;
  }
}

export async function checkCoachAIAccess(userId: string): Promise<boolean> {
  try {
    const { data: coachData, error } = await supabase
      .from('coaches')
      .select('ai_analysis_access')
      .eq('user_id', userId)
      .maybeSingle();

    if (error || !coachData) {
      return false;
    }

    return coachData.ai_analysis_access === true;
  } catch (error) {
    console.error('Error checking coach AI access:', error);
    return false;
  }
}

export async function isUserAdmin(userId: string): Promise<boolean> {
  try {
    const { data: userData, error } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .maybeSingle();

    if (error || !userData) {
      return false;
    }

    return userData.role === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

export async function isUserPartner(userId: string): Promise<boolean> {
  try {
    const { data: partnerData, error: partnerError } = await supabase
      .from('partners')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    return !partnerError && !!partnerData;
  } catch (error) {
    console.error('Error checking partner status:', error);
    return false;
  }
}

export async function getUserAvailableRoles(userId: string): Promise<UserRole[]> {
  const roles: UserRole[] = [];

  const [{ data: partnerData }, { data: coachData }, { data: respondentData }] = await Promise.all([
    supabase.from('partners').select('id').eq('user_id', userId).maybeSingle(),
    supabase.from('coaches').select('id').eq('user_id', userId).maybeSingle(),
    supabase.from('respondents').select('id').eq('user_id', userId).maybeSingle(),
  ]);

  if (partnerData) roles.push('partner');
  if (coachData) roles.push('coach');
  if (respondentData) roles.push('respondent');

  if (roles.length === 0) {
    const { data: userData } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .maybeSingle();
    if (userData?.role) roles.push(userData.role);
  }

  return roles;
}

export async function activatePartnerCoachAccount(userId: string): Promise<void> {
  const { data: partnerData } = await supabase
    .from('partners')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (!partnerData) throw new Error('Partner record not found');

  const { data: existingCoach } = await supabase
    .from('coaches')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (existingCoach) return;

  const code = 'HPOS' + Array.from({ length: 6 }, () =>
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 36)]
  ).join('');

  const { error } = await supabase
    .from('coaches')
    .insert({
      user_id: userId,
      partner_id: partnerData.id,
      tier: 'partner',
      assessment_code: code,
      ai_analysis_access: true,
      manual_ai_access: true,
      max_respondents: 999,
    });

  if (error) throw new Error('Failed to create coach account: ' + error.message);
}

export async function createCoachAccount(
  _auth: any,
  coachData: {
    email: string;
    password: string;
    fullName: string;
    tier: SubscriptionTier;
    partnerId: string;
    assessmentCode: string;
    aiAnalysisAccess?: boolean;
    manualAiAccess?: boolean;
    maxRespondents?: number;
    locale?: string;
  }
) {
  const { data: result, error: fnError } = await supabase.functions.invoke('create-coach', {
    body: {
      email: coachData.email,
      password: coachData.password,
      fullName: coachData.fullName,
      tier: coachData.tier,
      assessmentCode: coachData.assessmentCode,
      partnerId: coachData.partnerId || null,
      maxRespondents: coachData.maxRespondents || 50,
      locale: coachData.locale || "en",
    },
  });

  if (fnError) {
    let errMsg = 'Failed to create coach account';
    try {
      const errBody = await (fnError as any).context?.json?.();
      if (errBody?.error) errMsg = errBody.error;
    } catch {
      if (result?.error) errMsg = result.error;
    }
    throw new Error(errMsg);
  }

  return result.userId;
}

export async function checkUserAgreements(userId: string): Promise<boolean> {
  try {
    const { data: agreementsData, error } = await supabase
      .from('user_agreements')
      .select('privacy_accepted, terms_accepted, disclaimer_accepted, gdpr_accepted')
      .eq('user_id', userId)
      .maybeSingle();

    if (error || !agreementsData) {
      return false;
    }

    return Boolean(
      agreementsData.privacy_accepted &&
        agreementsData.terms_accepted &&
        agreementsData.disclaimer_accepted &&
        agreementsData.gdpr_accepted
    );
  } catch (error) {
    console.error('Error checking user agreements:', error);
    return false;
  }
}

export async function saveUserAgreements(
  userId: string,
  agreements: {
    privacyAccepted: boolean;
    termsAccepted: boolean;
    disclaimerAccepted: boolean;
    gdprAccepted: boolean;
  }
): Promise<void> {
  try {
    const now = new Date().toISOString();
    await supabase.from('user_agreements').upsert({
      user_id: userId,
      privacy_accepted: agreements.privacyAccepted,
      terms_accepted: agreements.termsAccepted,
      disclaimer_accepted: agreements.disclaimerAccepted,
      gdpr_accepted: agreements.gdprAccepted,
      privacy_accepted_at: now,
      terms_accepted_at: now,
      disclaimer_accepted_at: now,
      gdpr_accepted_at: now,
      updated_at: now,
    });
  } catch (error) {
    console.error('Error saving user agreements:', error);
    throw error;
  }
}
